/**
 * Page-lifetime owner of Google bootstrap and library work. A caller's bounded
 * wait is NOT cancellation of Google's script/import. Only an explicit retry
 * opens another wait; pending imports and uncertain bootstraps are always joined.
 */
import { isDesktopOffline } from '@/api/desktopSync';
import { GOOGLE_MAPS_API_KEY } from '@/config/constants';

const WAIT_MS = 10000;
const OBSERVE_MS = 100;
const libraries = new Map();
const imports = new Map();
const observers = new Set();
let bootstrap = null;
let callbackSequence = 0;

function notifyObservers() {
  [...observers].forEach((observe) => observe());
}

const checks = {
  maps: (lib) => typeof lib?.Map === 'function',
  core: (lib) => typeof lib?.LatLng === 'function' && typeof lib?.LatLngBounds === 'function',
  routes: (lib) => typeof lib?.Route?.computeRoutes === 'function',
  places: (lib) => typeof lib?.AutocompleteSuggestion?.fetchAutocompleteSuggestions === 'function'
    && typeof lib?.AutocompleteSessionToken === 'function',
  marker: (lib) => typeof lib?.AdvancedMarkerElement === 'function',
  geocoding: (lib) => typeof lib?.Geocoder === 'function',
  geometry: (lib) => typeof lib?.spherical?.computeDistanceBetween === 'function',
};

function failure(code, message, retryable = false) {
  return Object.assign(new Error(message), { code, retryable });
}

function offlineFailure() {
  return failure('offline', 'Google Maps assistance is unavailable in desktop offline mode.');
}

function namespace(name) {
  const maps = window.google?.maps;
  if (name === 'places' || name === 'geometry' || name === 'routes') return maps?.[name];
  if (name === 'marker') return maps?.marker || maps;
  return maps;
}

function scripts() {
  return [...document.querySelectorAll('script[src]')].filter((script) => {
    try {
      const url = new URL(script.src, document.baseURI);
      return url.hostname === 'maps.googleapis.com' && url.pathname === '/maps/api/js';
    } catch {
      return false;
    }
  });
}

function ensureBootstrap(retry) {
  if (typeof window.google?.maps?.importLibrary === 'function') return;
  let existing = scripts();
  if (bootstrap?.status === 'error') {
    // Script error is conclusive only before Google started creating Maps state.
    // Never remove somebody else's script or reset somebody else's globals.
    if (window.google?.maps || bootstrap.called || existing.some((s) => s !== bootstrap.script)) {
      throw failure('unsafe_partial', 'Google Maps is partly loaded and cannot safely be restarted here. Your work is preserved.');
    }
    if (!retry) throw bootstrap.error;
    bootstrap.script.remove();
    if (window[bootstrap.callbackName] === bootstrap.callback) delete window[bootstrap.callbackName];
    bootstrap = null;
    existing = scripts();
  }
  if (bootstrap || existing.length || window.google?.maps) return;
  if (!GOOGLE_MAPS_API_KEY) {
    throw failure('missing_key', 'Google Maps is not configured for this app.');
  }

  let callbackName;
  do { callbackName = `__lvbGoogleMapsReady${++callbackSequence}`; } while (callbackName in window);
  const script = document.createElement('script');
  const record = { script, callbackName, status: 'pending', called: false };
  record.callback = () => {
    record.called = true;
    record.status = 'ready';
    if (window[callbackName] === record.callback) delete window[callbackName];
    notifyObservers();
  };
  window[callbackName] = record.callback;
  script.async = true;
  script.defer = true;
  const params = new URLSearchParams({ key: GOOGLE_MAPS_API_KEY, loading: 'async', v: 'weekly', callback: callbackName });
  script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
  script.nonce = document.querySelector('script[nonce]')?.nonce || '';
  script.onerror = () => {
    record.status = 'error';
    record.error = failure('script_failed', 'Google Maps could not load. Try again when your connection is available.', true);
    notifyObservers();
  };
  bootstrap = record;
  document.head.appendChild(script);
}

function importedLibrary(record, name, retry) {
  if (record.importState === 'pending') return;
  if (record.importState === 'failed' && !retry) throw record.importError;
  if (record.importState === 'ready') return;
  const importer = window.google?.maps?.importLibrary;
  if (typeof importer !== 'function') return;
  record.importState = 'pending';
  // Capture the underlying operation independently of this consumer wait.
  record.importPromise = Promise.resolve().then(() => {
    if (isDesktopOffline()) throw offlineFailure();
    return importer.call(window.google.maps, name);
  }).then((value) => {
    const usable = checks[name](value);
    if (!usable) {
      throw failure('library_unavailable', 'The required Google Maps capability did not become available.', true);
    }
    record.value = value;
    record.importState = 'ready';
    notifyObservers();
  }).catch((error) => {
    record.importState = 'failed';
    record.importError = error?.code === 'offline' ? error
      : failure('library_failed', 'A Google Maps capability could not load. You can try again.', true);
    notifyObservers();
  });
}

function loadLibrary(name, { retry, requireImporter }) {
  let record = libraries.get(name);
  if (!record) {
    record = {};
    libraries.set(name, record);
  }
  const importName = name;
  if (!imports.has(importName)) imports.set(importName, {});
  const work = imports.get(importName);
  // Importer-only readiness is also bounded: a legacy-ready namespace must not
  // let APIProvider install a competing bootstrap.
  const attemptKey = requireImporter ? 'providerAttempt' : 'attempt';
  const previous = record[attemptKey];
  if (previous && (previous.state === 'pending' || !retry)) return previous.promise;
  const attempt = { state: 'pending' };
  record[attemptKey] = attempt;
  attempt.promise = new Promise((resolve, reject) => {
    const deadline = Date.now() + WAIT_MS;
    let timer;
    let done = false;
    let importRetryAllowed = retry;
    let bootstrapRetryAllowed = retry;
    const finish = (error, value) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      observers.delete(observe);
      attempt.state = error ? 'failed' : 'ready';
      if (error) reject(error); else resolve(value);
    };
    const observe = () => {
      if (done) return;
      clearTimeout(timer);
      if (isDesktopOffline()) { finish(offlineFailure()); return; }
      const available = checks[name](work.value) ? work.value : namespace(name);
      const hasImporter = typeof window.google?.maps?.importLibrary === 'function';
      if (checks[name](available) && (!requireImporter || hasImporter)) {
        finish(null, available);
        return;
      }
      try {
        ensureBootstrap(bootstrapRetryAllowed);
        bootstrapRetryAllowed = false;
        if (!checks[name](available)) {
          if (work.importState === 'ready') {
            if (!importRetryAllowed) throw failure('library_unavailable', 'The required Google Maps capability did not become available.', true);
            work.importState = 'failed';
          }
          if (work.importState === 'failed' && !importRetryAllowed) throw work.importError;
          importedLibrary(work, importName, importRetryAllowed);
          // An explicit click permits one import, not a retry loop within a wait.
          if (work.importState === 'pending') importRetryAllowed = false;
        }
      } catch (error) { finish(error); return; }
      if (Date.now() >= deadline) {
        finish(failure('timeout', 'Google Maps did not become ready in time. Try again to check the existing load.', true));
        return;
      }
      timer = setTimeout(observe, Math.min(OBSERVE_MS, deadline - Date.now()));
    };
    observers.add(observe);
    observe();
  });
  return attempt.promise;
}

/** Required names resolve to library objects, never to mere script presence.
 * Retry is explicit. Consumers retire their own UI work, not this shared SDK.
 */
export function loadGoogleMapsLibraries(names, { retry = false, requireImporter = false } = {}) {
  if (isDesktopOffline()) return Promise.reject(offlineFailure());
  if (!Array.isArray(names) || names.some((name) => !Object.hasOwn(checks, name))) {
    return Promise.reject(failure('unsupported_library', 'An unsupported Google Maps capability was requested.'));
  }
  const requested = [...new Set(names)];
  return Promise.all(requested.map((name) => loadLibrary(name, { retry, requireImporter })
    .then((value) => [name, value]))).then((entries) => Object.fromEntries(entries));
}
